import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCalendar, FiClock, FiUser, FiTag, FiArrowRight, FiBookOpen, FiSearch, FiFilter } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import dataService from '../services/dataService';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await dataService.getBlogs();
        setBlogs(data);
      } catch (error) {
        console.error('Failed to load blogs:', error);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    const loadCategories = async () => {
      try {
        // For now, use static categories as fallback since API might not have them
        const { blogCategories: staticCategories } = await import('../data/siteContent');
        setBlogCategories(staticCategories);
      } catch (error) {
        console.error('Failed to load blog categories:', error);
        setBlogCategories([]);
      }
    };

    loadBlogs();
    loadCategories();
  }, []);

  const featuredPosts = blogs.filter(post => post.featured || post.is_featured);
  const recentPosts = [...blogs].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)).slice(0, 5);
  const filteredPosts = blogs.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category.toLowerCase().replace(/\s+/g, '-') === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 min-h-screen">
      <SEO
        pageType="blog"
        title="Health & Wellness Blog | Natural Healing Insights | JIVA NADI"
        description="Read expert articles on natural healing, chiropractic care, and holistic wellness. Get valuable insights from Dr. Saurabh Singh on pain relief and health optimization."
        keywords="health blog, wellness articles, chiropractic tips, natural healing, pain relief advice, holistic health, wellness insights"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Expert Insights on Natural Healing"
        subtitle="Health & Wellness Blog"
        description="Discover comprehensive guides, treatment insights, and wellness tips from Dr. Saurabh Singh and our expert team. Stay informed about the latest in holistic healthcare."
        image="https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=600&fit=crop"
        badge="Health & Wellness Blog"
        primaryButton={{
          text: "Read Articles",
          href: "#blog-posts"
        }}
        secondaryButton={{
          text: "Browse Categories",
          href: "#categories"
        }}
        trustIndicators={[
          { value: blogs.length.toString(), label: "Expert Articles" },
          { value: "15+", label: "Years Experience" },
          { value: "30K+", label: "Patients Helped" }
        ]}
      />

      {/* ===== MAIN CONTENT: 2-COLUMN LAYOUT ===== */}
      <section id="blog-posts" className="py-20 lg:py-28 -mt-16 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <SafeIcon icon={FiSearch} className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search articles, topics, treatments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-5 py-4 bg-white border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-300 text-gray-900 placeholder-gray-400 shadow-lg font-medium"
              />
            </div>
          </motion.div>

          {/* Categories Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <div className="md:bg-white md:rounded-2xl md:p-4 md:shadow-lg md:border md:border-gray-100">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 md:flex-wrap md:justify-center md:overflow-x-visible md:pb-0">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`flex-shrink-0 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-300 border-none outline-none focus:outline-none ${
                    selectedCategory === 'all'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>All Articles</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      selectedCategory === 'all' ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {blogs.length}
                    </span>
                  </div>
                </button>

                {blogCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex-shrink-0 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-300 border-none outline-none focus:outline-none ${
                      selectedCategory === category.id
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{category.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        selectedCategory === category.id ? 'bg-white/20' : 'bg-gray-200'
                      }`}>
                        {category.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
            
            {/* ===== LEFT: MAIN CONTENT (8 columns) ===== */}
            <div className="lg:col-span-8 space-y-6">
              
              {/* Articles Grid */}
              {filteredPosts.length > 0 ? (
                filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                    className="group"
                  >
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl hover:border-emerald-200 transition-all duration-500 hover:-translate-y-1">
                        <div className="flex flex-col md:flex-row">
                          
                          {/* Image */}
                          <div className="md:w-2/5 relative h-56 md:h-auto overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 to-transparent z-10" />
                            <img
                              src={post.image}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                            
                            {/* Category Badge */}
                            <div className="absolute top-4 left-4 z-20">
                              <span className="inline-flex items-center bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
                                <span className="text-xs font-bold text-gray-900">{post.category}</span>
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="md:w-3/5 p-5 lg:p-6 flex flex-col">
                            {/* Meta */}
                            <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                              <div className="flex items-center gap-1.5">
                                <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="font-semibold">{new Date(post.publishedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}</span>
                              </div>
                              <div className="w-1 h-1 bg-gray-300 rounded-full" />
                              <div className="flex items-center gap-1.5">
                                <SafeIcon icon={FiClock} className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="font-semibold">{post.readTime}</span>
                              </div>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl lg:text-2xl font-black text-gray-900 mb-2 leading-tight group-hover:text-emerald-600 transition-colors line-clamp-2">
                              {post.title}
                            </h3>

                            {/* Excerpt */}
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2 flex-1">
                              {post.excerpt}
                            </p>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white font-bold text-xs">{post.author.charAt(0)}</span>
                                </div>
                                <span className="text-sm font-semibold text-gray-900">{post.author}</span>
                              </div>

                              <div className="inline-flex items-center gap-2 text-emerald-600 font-bold text-sm group-hover:gap-3 transition-all">
                                <span>Read</span>
                                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))
              ) : (
                /* No Results */
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-md"
                >
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <SafeIcon icon={FiBookOpen} className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">No Articles Found</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
                    We couldn't find any articles matching your search criteria. Try adjusting your filters.
                  </p>
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setSearchTerm('');
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-xl"
                  >
                    Reset Filters
                  </button>
                </motion.div>
              )}
            </div>

            {/* ===== RIGHT: SIDEBAR (4 columns) ===== */}
            <aside className="lg:col-span-4">
              <div className="space-y-6 lg:sticky lg:top-24">
              
                {/* Recent Posts Widget */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-white rounded-2xl p-6 lg:p-7 shadow-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900">Recent Posts</h3>
                  </div>

                  <div className="space-y-4">
                    {recentPosts.map((post, index) => (
                      <div key={post.id}>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="group block"
                        >
                          <div className="flex gap-3 items-start">
                            {/* Thumbnail */}
                            <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-sm text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-2 mb-1.5 leading-tight">
                                {post.title}
                              </h4>
                              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                                <span>{new Date(post.publishedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                        {index < recentPosts.length - 1 && (
                          <div className="mt-4 border-b border-gray-100" />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Newsletter CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 lg:p-7 shadow-xl text-white"
                >
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-5">
                    <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-black mb-3">Stay Updated</h3>
                  <p className="text-emerald-100 mb-5 text-sm leading-relaxed">
                    Get the latest health insights delivered to your inbox weekly.
                  </p>
                  <button className="w-full bg-white hover:bg-emerald-50 text-emerald-600 px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-xl">
                    Subscribe Now
                  </button>
                </motion.div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;